import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { partsApi, formatPrice, getFileUrl, PART_CATEGORIES, PART_CONDITIONS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import QRCode from "react-qr-code";
import imageCompression from 'browser-image-compression';
import { 
  ArrowLeft,
  Edit,
  Trash2,
  Camera,
  Upload,
  X,
  QrCode,
  MapPin,
  Package,
  Calendar,
  Hash,
  Printer,
  Download,
  Link as LinkIcon,
  Eye
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function PartDetailPage() {
  const { partId } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [part, setPart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const photoInputRef = useRef(null);

  useEffect(() => {
    fetchPart();
  }, [partId]);

  const fetchPart = async () => {
    try {
      const data = await partsApi.getById(partId);
      setPart(data);
    } catch (error) {
      toast.error("Piesa nu a fost gasita");
      navigate("/parts");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await partsApi.delete(partId);
      toast.success("Piesa a fost stearsa");
      navigate("/parts");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la stergere");
    }
  };

  // Compress image with reasonable quality
  const compressImage = async (file, maxWidth = 1600, quality = 0.75) => {
    try {
      const options = {
        maxSizeMB: 1.0, // Max 1MB - good balance
        maxWidthOrHeight: maxWidth,
        useWebWorker: true,
        initialQuality: quality,
      };
      const compressedFile = await imageCompression(file, options);
      console.log(`Compressed ${file.name}: ${(file.size/1024).toFixed(0)}KB -> ${(compressedFile.size/1024).toFixed(0)}KB`);
      return compressedFile;
    } catch (error) {
      console.error('Compression error:', error);
      return file;
    }
  };

  // Handle multiple photo upload with compression
  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    setUploading(true);
    const totalFiles = files.length;
    let uploadedCount = 0;
    let failedCount = 0;

    toast.info(`Incarcare ${totalFiles} foto cu compresie maxima...`);

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setUploadProgress({ current: i + 1, total: totalFiles });

      try {
        // Reasonable compression
        let fileToUpload = file;
        if (file.type.startsWith('image/')) {
          toast.info(`Compresie ${i + 1}/${totalFiles}: ${file.name}...`);
          fileToUpload = await compressImage(file, 1600, 0.75);
        }

        console.log(`Upload ${i + 1}/${totalFiles}: ${file.name} (${(fileToUpload.size/1024).toFixed(0)}KB)`);
        await partsApi.uploadPhoto(partId, fileToUpload);
        uploadedCount++;
      } catch (error) {
        console.error('Upload error:', error);
        failedCount++;
      }
    }

    if (uploadedCount > 0) {
      toast.success(`${uploadedCount} foto incarcate cu succes!`);
    }
    if (failedCount > 0) {
      toast.error(`${failedCount} foto au esuat`);
    }

    setUploading(false);
    setUploadProgress({ current: 0, total: 0 });
    if (photoInputRef.current) photoInputRef.current.value = "";
    fetchPart();
  };

  const handleDeletePhoto = async (photoUrl) => {
    const filename = photoUrl.split("/").pop();
    try {
      await partsApi.deletePhoto(partId, filename);
      toast.success("Foto stearsa");
      fetchPart();
    } catch (error) {
      toast.error("Eroare la stergere");
    }
  };

  const qrUrl = `${window.location.origin}/part/${part?.part_id}`;

  const handlePrintQR = () => {
    const printWindow = window.open("", "_blank");
    const qrElement = document.getElementById(`qr-part-${part.part_id}`);
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>QR Code - ${part.name}</title>
          <style>
            body {
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              min-height: 100vh;
              margin: 0;
              font-family: Arial, sans-serif;
              background: white;
            }
            .container { text-align: center; padding: 20px; }
            h1 { font-size: 24px; margin-bottom: 8px; }
            .part-name { font-size: 18px; font-weight: bold; margin-bottom: 8px; }
            .part-compat { font-size: 14px; color: #666; margin-bottom: 16px; }
            .qr-wrapper { background: white; padding: 16px; border: 2px solid #000; display: inline-block; }
            .part-id { margin-top: 16px; font-size: 18px; font-weight: bold; font-family: monospace; }
            .location { margin-top: 8px; font-size: 14px; color: #666; }
          </style>
        </head>
        <body>
          <div class="container">
            <h1>SCUTERSH PARTS</h1>
            <div class="part-name">${part.name}</div>
            <div class="part-compat">${part.compatible_brands.join(", ")} - ${part.compatible_models.join(", ")}</div>
            <div class="qr-wrapper">${qrElement?.innerHTML || ""}</div>
            <div class="part-id">${part.part_id}</div>
            ${part.storage_location ? `<div class="location">Raft: ${part.storage_location}</div>` : ""}
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const defaultImage = "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop";

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20">
          <div className="animate-pulse text-primary font-mono">Incarcare...</div>
        </div>
      </Layout>
    );
  }

  if (!part) {
    return (
      <Layout>
        <div className="text-center py-20">
          <p className="text-muted-foreground">Piesa nu a fost gasita</p>
        </div>
      </Layout>
    );
  }

  const categoryInfo = PART_CATEGORIES[part.category] || { label: part.category, icon: "📦" };
  const conditionInfo = PART_CONDITIONS[part.condition] || { label: part.condition, color: "bg-gray-500" };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto space-y-6" data-testid="part-detail-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="flex items-start gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/parts")}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-sm text-primary">{part.part_id}</span>
                <span className="text-xl">{categoryInfo.icon}</span>
                <span className={`px-2 py-1 rounded text-xs text-white ${conditionInfo.color}`}>
                  {conditionInfo.label}
                </span>
                {part.quantity === 0 && (
                  <span className="px-2 py-1 rounded text-xs bg-red-500 text-white">EPUIZAT</span>
                )}
              </div>
              <h1 className="font-heading font-black text-2xl md:text-3xl">
                {part.name}
              </h1>
              <p className="text-muted-foreground font-mono text-sm mt-1">
                {part.compatible_brands.join(", ")} - {part.compatible_models.join(", ")}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to={`/parts/${partId}/edit`}>
              <Button variant="outline" data-testid="btn-edit">
                <Edit className="w-4 h-4 mr-2" />
                Modifica
              </Button>
            </Link>
            {isAdmin() && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className="text-destructive hover:text-destructive" data-testid="btn-delete">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Stergi aceasta piesa?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Aceasta actiune va sterge definitiv "{part.name}" ({part.part_id}).
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Anuleaza</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
                      Sterge
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Gallery */}
          <div className="lg:col-span-2 space-y-6">
            {/* Photo Gallery */}
            <Card>
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Camera className="w-5 h-5 text-primary" />
                    Fotografii
                  </CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => photoInputRef.current?.click()}
                    disabled={uploading}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {uploading ? `${uploadProgress.current}/${uploadProgress.total}...` : "Adauga Foto"}
                  </Button>
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handlePhotoUpload}
                    className="hidden"
                  />
                </div>
              </CardHeader>
              <CardContent>
                {/* Upload Progress */}
                {uploading && (
                  <div className="mb-4 p-4 bg-primary/10 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium text-primary">
                        Compresie & Incarcare...
                      </p>
                      <p className="text-sm font-mono text-primary">
                        {uploadProgress.current}/{uploadProgress.total}
                      </p>
                    </div>
                    <Progress value={(uploadProgress.current / uploadProgress.total) * 100} className="h-2" />
                    <p className="text-xs text-muted-foreground mt-2 text-center">
                      Fotografiile sunt comprimate automat (max 200KB)
                    </p>
                  </div>
                )}
                
                {part.photos?.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {part.photos.map((photo, idx) => (
                      <div key={idx} className="relative group aspect-[4/3] rounded-lg overflow-hidden bg-secondary">
                        <img
                          src={getFileUrl(photo)}
                          alt={`Foto ${idx + 1}`}
                          className="w-full h-full object-cover"
                        />
                        <button
                          onClick={() => handleDeletePhoto(photo)}
                          className="absolute top-2 right-2 w-8 h-8 bg-black/70 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-4 h-4 text-white" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="aspect-video rounded-lg overflow-hidden bg-secondary relative">
                    <img
                      src={defaultImage}
                      alt="Default"
                      className="w-full h-full object-cover opacity-50"
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <p className="text-muted-foreground text-sm">Nicio fotografie</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Compatibility */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Package className="w-5 h-5 text-primary" />
                  Compatibilitate
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Marci</p>
                  <div className="flex flex-wrap gap-2">
                    {part.compatible_brands.map((brand, idx) => (
                      <span key={idx} className="px-3 py-1 bg-secondary rounded-full text-sm font-medium">
                        {brand}
                      </span>
                    ))}
                  </div>
                </div>
                <Separator />
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Modele</p>
                  <div className="flex flex-wrap gap-2">
                    {part.compatible_models.map((model, idx) => (
                      <span key={idx} className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium">
                        {model}
                      </span>
                    ))}
                  </div>
                </div>
                {part.compatible_years?.length > 0 && (
                  <>
                    <Separator />
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Ani</p>
                      <p className="font-mono text-sm">
                        {part.compatible_years.sort((a, b) => a - b).join(", ")}
                      </p>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Source Scooter */}
            {part.source_scooter_id && (
              <Card className="border-primary/30 bg-primary/5">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <LinkIcon className="w-5 h-5 text-primary" />
                    Scooter sursa
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-mono text-sm text-muted-foreground">{part.source_scooter_id}</p>
                      {part.source_scooter_name && (
                        <p className="font-bold">{part.source_scooter_name}</p>
                      )}
                    </div>
                    <Link to={`/stock/${part.source_scooter_id}`}>
                      <Button variant="outline" size="sm">
                        <Eye className="w-4 h-4 mr-2" />
                        Vezi scooter
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Notes */}
            {part.notes && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Note</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground whitespace-pre-wrap">{part.notes}</p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column - Details & QR */}
          <div className="space-y-6">
            {/* Price & Stock */}
            <Card className="border-primary/30 bg-primary/5">
              <CardContent className="p-6">
                <div className="text-center">
                  <p className="text-4xl font-black text-primary">{formatPrice(part.price)}</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Cantitate: <span className={`font-bold ${part.quantity === 0 ? 'text-red-500' : 'text-green-500'}`}>
                      {part.quantity} buc
                    </span>
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Details */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Detalii</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground flex items-center gap-2">
                    <Hash className="w-4 h-4" />
                    Numar piesa
                  </span>
                  <span className="font-mono">{part.part_number || "-"}</span>
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    Categorie
                  </span>
                  <span>{categoryInfo.label}</span>
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Locatie (Raft)
                  </span>
                  <span className="font-mono font-bold text-primary">{part.storage_location || "-"}</span>
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Adaugat
                  </span>
                  <span className="font-mono text-sm">{part.created_at?.split("T")[0] || "-"}</span>
                </div>
              </CardContent>
            </Card>

            {/* QR Code */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <QrCode className="w-5 h-5 text-primary" />
                  Cod QR
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col items-center gap-4">
                <div className="bg-white p-4 rounded-lg" id={`qr-part-${part.part_id}`}>
                  <QRCode value={qrUrl} size={150} />
                </div>
                <p className="text-xs text-muted-foreground text-center font-mono">
                  {part.part_id}
                </p>
                <div className="flex gap-2 w-full">
                  <Button variant="outline" size="sm" className="flex-1" onClick={handlePrintQR}>
                    <Printer className="w-4 h-4 mr-2" />
                    Printeaza
                  </Button>
                  <Button variant="outline" size="sm" className="flex-1" asChild>
                    <a href={qrUrl} target="_blank" rel="noopener noreferrer">
                      <Download className="w-4 h-4 mr-2" />
                      Vezi
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
}
